import React, { useEffect, useState } from 'react'
import { Avatar, Box, IconButton, Paper, Slide, Typography } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { WA } from './whatsappTheme'
import profilePic from '../../../assets/defaultAvatar.png'
import { getChatNotificationsEnabled } from './chatWidgetPrefs'
import { isEndMessage, isSystemMessage } from './helpDeskSession'
import {
  initChatNotificationSound,
  playChatNotificationSound,
} from './chatNotificationSound'

const TOAST_EVENT = 'signwise-chat-toast'
const DEDUPE_MS = 4000
const recentNotifications = new Map()

const buildNotificationKey = (msg) => {
  const text = String(msg?.message || msg?.text || '')
    .replace(/<(.|\n)*?>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  return [
    msg?.senderId ?? '',
    msg?.conversationId ?? '',
    msg?.contentType ?? 'text',
    text,
  ].join('|')
}

export const notifyHelpDeskStatusChange = (
  { type, whenChatOpen = false } = {},
) => {
  if (!getChatNotificationsEnabled()) return
  if (type !== 'online' && type !== 'offline') return

  const preview =
    type === 'online'
      ? 'Help Desk is back online. An agent is available to continue your chat.'
      : 'Help Desk is currently offline. You can continue to leave messages — an agent will reply when they are back online.'

  const dedupeKey = `helpdesk-status-${type}`
  const now = Date.now()
  const lastShown = recentNotifications.get(dedupeKey)
  if (lastShown && now - lastShown < DEDUPE_MS) return
  recentNotifications.set(dedupeKey, now)

  playChatNotificationSound({ whenChatOpen })
  window.dispatchEvent(
    new CustomEvent(TOAST_EVENT, {
      detail: {
        id: `${dedupeKey}-${now}`,
        name: 'Help Desk',
        photo: null,
        preview,
      },
    }),
  )
}

export const notifyIncomingChatMessage = (
  msg,
  { myUserId, fallbackTitle = 'New message', whenChatOpen = false } = {},
) => {
  if (!getChatNotificationsEnabled()) return
  if (!msg || Number(msg.senderId) === Number(myUserId)) return
  if (
    isEndMessage({
      contentType: msg.contentType,
      text: msg.message,
      message: msg.message,
    })
  ) {
    return
  }
  const systemText = String(msg?.message || msg?.text || '')
    .replace(/<(.|\n)*?>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  const isJoinOrAddSystem =
    /was added to the chat$/i.test(systemText) ||
    /joined the chat$/i.test(systemText)
  if (isSystemMessage({ contentType: msg.contentType, message_type: msg.contentType }) && !isJoinOrAddSystem) {
    return
  }

  const dedupeKey = buildNotificationKey(msg)
  const now = Date.now()
  const lastShown = recentNotifications.get(dedupeKey)
  if (lastShown && now - lastShown < DEDUPE_MS) return
  recentNotifications.set(dedupeKey, now)

  const sender = msg.sender || {}
  const name =
    sender.full_name ||
    [sender.first_name, sender.last_name].filter(Boolean).join(' ').trim() ||
    fallbackTitle

  playChatNotificationSound({ whenChatOpen })
  window.dispatchEvent(
    new CustomEvent(TOAST_EVENT, {
      detail: {
        id: dedupeKey,
        name,
        photo: sender.profile_pic || null,
      },
    }),
  )
}

export default function ChatIncomingToast({ onOpen }) {
  const [toasts, setToasts] = useState([])

  useEffect(() => {
    initChatNotificationSound()
  }, [])

  useEffect(() => {
    const onToast = (event) => {
      const item = event.detail
      if (!item) return
      setToasts((prev) => {
        if (prev.some((toast) => toast.id === item.id)) return prev
        return [item, ...prev].slice(0, 3)
      })
      window.setTimeout(() => {
        setToasts((prev) => prev.filter((toast) => toast.id !== item.id))
      }, 5000)
    }
    window.addEventListener(TOAST_EVENT, onToast)
    return () => window.removeEventListener(TOAST_EVENT, onToast)
  }, [])

  const dismiss = (id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 24,
        right: 24,
        zIndex: 2000,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        pointerEvents: 'none',
        width: 320,
        maxWidth: 'calc(100vw - 32px)',
      }}
    >
      {toasts.map((toast) => (
        <Slide key={toast.id} direction="left" in mountOnEnter unmountOnExit>
          <Paper
            elevation={8}
            onClick={() => {
              if (onOpen) onOpen()
              dismiss(toast.id)
            }}
            sx={{
              pointerEvents: 'auto',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.25,
              p: 1.25,
              borderRadius: '14px',
              border: `1px solid ${WA.border}`,
              boxShadow: WA.shadow,
              cursor: onOpen ? 'pointer' : 'default',
              bgcolor: '#fff',
            }}
          >
            <Avatar
              src={typeof toast.photo === 'string' ? toast.photo : profilePic}
              alt={toast.name}
              sx={{ width: 40, height: 40, mt: 0.25 }}
            />
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700, color: WA.text }}>
                {toast.name}
              </Typography>
              <Typography variant="body2" sx={{ color: WA.meta, mt: 0.25 }}>
                {toast.preview || 'sent you a new message'}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation()
                dismiss(toast.id)
              }}
              sx={{ color: WA.meta, mt: -0.5, mr: -0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Paper>
        </Slide>
      ))}
    </Box>
  )
}
